import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SCALE = [
  { v: 1, l: "Struggling" }, { v: 2, l: "Low" }, { v: 3, l: "Okay" }, { v: 4, l: "Strong" }, { v: 5, l: "Thriving" },
];

export default function Assessment() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [domains, setDomains] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/assessment/domains").then((r) => setDomains(r.data)); }, []);

  const current = domains[step];
  const progress = domains.length ? Math.round((step / domains.length) * 100) : 0;

  const pick = (val) => {
    const next = { ...answers, [current.key]: val };
    setAnswers(next);
    if (step + 1 < domains.length) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      submit(next);
    }
  };

  const submit = async (final) => {
    if (!user) {
      localStorage.setItem("lwn_pending_assessment", JSON.stringify(final));
      toast("Create a free account to unlock your AI insight report.");
      nav("/signup");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessment/submit", { answers: final });
      nav("/dashboard", { state: { tab: "assessment", latest: data.result_id } });
      toast.success("Your LifePrints report is ready!");
    } catch {
      toast.error("Could not submit. Please try again.");
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const pending = localStorage.getItem("lwn_pending_assessment");
    if (user && pending) {
      localStorage.removeItem("lwn_pending_assessment");
      submit(JSON.parse(pending));
    }
    // eslint-disable-next-line
  }, [user]);

  if (submitting) {
    return (
      <div className="min-h-screen bg-[#1E3A2B] flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-serif-d text-[#DCBB6C] text-3xl italic-emph animate-pulse">Coach Nata is reading your LifePrints…</p>
          <p className="text-[#FAF4E9]/60 mt-4">Generating your personalized insight report.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF4E9] min-h-screen">
      <Nav />
      <section className="max-w-3xl mx-auto px-6 pt-40 pb-24">
        {!started ? (
          <div className="text-center">
            <p className="eyebrow">The LifePrints™ Assessment</p>
            <h1 className="font-serif-d text-4xl md:text-6xl text-[#1E3A2B] tracking-tighter mt-4">A 10-domain map of <span className="italic-emph text-[#C0953A]">where you are.</span></h1>
            <p className="text-[#21302A]/70 mt-6 max-w-lg mx-auto">Rate yourself across ten areas of life. At the end, Coach Nata generates a personalized, faith-rooted insight report — and your next step.</p>
            <button data-testid="quiz-start-button" onClick={() => setStarted(true)} disabled={!domains.length}
              className="mt-10 bg-[#1E3A2B] text-[#FAF4E9] hover:bg-[#2E5240] transition-colors px-10 py-4 uppercase tracking-widest text-xs font-medium disabled:opacity-50">
              Begin Assessment →
            </button>
          </div>
        ) : current ? (
          <div data-testid="quiz-step">
            <div className="h-1 bg-[#21302A]/10 mb-12">
              <div className="h-full bg-[#C0953A] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <p className="eyebrow">Domain {step + 1} of {domains.length}</p>
            <h2 className="font-serif-d text-3xl md:text-5xl text-[#1E3A2B] tracking-tight mt-4">{current.label}</h2>
            <p className="text-[#21302A]/60 mt-4">How would you describe this area of your life right now?</p>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mt-10">
              {SCALE.map((s) => (
                <button key={s.v} data-testid={`quiz-option-${s.v}`} onClick={() => pick(s.v)}
                  className="border border-[#21302A]/15 bg-white hover:bg-[#1E3A2B] hover:text-[#FAF4E9] transition-colors p-5 flex flex-col items-center gap-2 group">
                  <span className="font-serif-d text-3xl text-[#C0953A] group-hover:text-[#DCBB6C]">{s.v}</span>
                  <span className="text-xs uppercase tracking-widest">{s.l}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-10 text-xs uppercase tracking-widest text-[#21302A]/50 hover:text-[#C0953A]">← Previous</button>
            )}
          </div>
        ) : null}
      </section>
      <Footer />
    </div>
  );
}
